import { useEffect, useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { Card, CardStats, CardTransaction } from '../../../hooks/useCards';

// ── Module-level stats cache (persists across open/close cycles, cleared on refresh) ──
interface StatsCacheEntry { stats: CardStats; timestamp: number }
const statsCache = new Map<string, StatsCacheEntry>();
const CACHE_TTL_MS = 60_000; // 60 seconds

interface LinkedAccount {
  id: string;
  name: string;
  bank_name: string;
  color: string;
}

interface CardPaymentRecord {
  id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  notes: string | null;
  affects_account_balance: boolean;
  billing_cycle_start: string | null;
  source_account_id: string | null;
  account_name?: string;
}

interface CardDetailModalProps {
  open: boolean;
  card: Card | null;
  onClose: () => void;
  onEdit: (card: Card) => void;
  onArchive: (card: Card) => void;
  getStats: (card: Card) => Promise<CardStats>;
  onPayFull?: (card: Card, stats: CardStats) => void;
  onPayPartial?: (card: Card, stats: CardStats) => void;
}

const CARD_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  credit:   { label: 'Crédito',  icon: 'ri-bank-card-2-line', color: 'text-emerald-700', bg: 'bg-emerald-100/30' },
  debit:    { label: 'Débito',   icon: 'ri-bank-card-line',   color: 'text-sky-200',     bg: 'bg-white/10' },
  multiple: { label: 'Múltiplo', icon: 'ri-swap-line',         color: 'text-amber-200',   bg: 'bg-white/10' },
};

const BRAND_CONFIG: Record<string, { label: string; shortLabel: string; bg: string; textColor: string }> = {
  visa: { label: 'Visa', shortLabel: 'VISA', bg: '#1a1f71', textColor: '#fff' },
  mastercard: { label: 'Mastercard', shortLabel: 'MC', bg: 'linear-gradient(90deg,#eb001b,#f79e1b)', textColor: '#fff' },
  elo: { label: 'Elo', shortLabel: 'ELO', bg: '#ffcb05', textColor: '#111' },
  amex: { label: 'American Express', shortLabel: 'AMEX', bg: '#007bc1', textColor: '#fff' },
  hipercard: { label: 'Hipercard', shortLabel: 'HIPER', bg: '#b3131b', textColor: '#fff' },
  outro: { label: 'Outra', shortLabel: 'CARD', bg: '#e5e7eb', textColor: '#374151' },
};

const PAYMENT_METHOD_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  manual: { label: 'Pagamento manual', icon: 'ri-hand-coin-line', color: 'text-gray-600' },
  automatic_debit: { label: 'Débito automático', icon: 'ri-loop-right-line', color: 'text-emerald-600' },
  external: { label: 'Pagamento externo', icon: 'ri-external-link-line', color: 'text-gray-600' },
};

const PAYMENT_METHOD_DISPLAY: Record<string, { label: string; icon: string }> = {
  pix:              { label: 'Pix',               icon: 'ri-qr-code-line' },
  ted:              { label: 'TED/Transferência',  icon: 'ri-exchange-dollar-line' },
  boleto:           { label: 'Boleto',             icon: 'ri-barcode-line' },
  conta:            { label: 'Débito em conta',    icon: 'ri-bank-line' },
  dinheiro:         { label: 'Dinheiro',           icon: 'ri-money-dollar-circle-line' },
  debito_automatico:{ label: 'Débito automático',  icon: 'ri-loop-right-line' },
};

const CURRENCY_MAP: Record<string, string> = { BRL: 'BRL', USD: 'USD', EUR: 'EUR' };

const fmtDate = (d: string) =>
  new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });

const STATUS_STYLE: Record<string, string> = {
  pago: 'bg-emerald-100 text-emerald-700',
  pendente: 'bg-amber-100 text-amber-700',
};

export const CardDetailModal = ({
  open, card, onClose, onEdit, onArchive, getStats, onPayFull, onPayPartial,
}: CardDetailModalProps) => {
  const { user } = useAuth();
  const [stats, setStats] = useState<CardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<'fatura' | 'todas' | 'pagamentos'>('fatura');
  const [linkedAccount, setLinkedAccount] = useState<LinkedAccount | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<CardPaymentRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Track which card was last opened to invalidate cache on card data change
  const lastOpenedCardIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (open && card) {
      setLinkedAccount(null);
      setPaymentHistory([]);
      setTab('fatura');

      // ── Check cache before issuing new queries ──────────────────────────
      const cached = statsCache.get(card.id);
      const now = Date.now();
      const cacheHit = cached && now - cached.timestamp < CACHE_TTL_MS;

      if (cacheHit && lastOpenedCardIdRef.current === card.id) {
        // Serve from cache: no spinner needed
        setStats(cached.stats);
        setLoading(false);
        // Still refresh payment history (lightweight, needed for the payments tab)
        setHistoryLoading(true);
        fetchPaymentHistory(card.id).then((history) => {
          setPaymentHistory(history);
          setHistoryLoading(false);
        });
      } else {
        // Cache miss or card changed: fetch fresh data
        setStats(null);
        setLoading(true);
        setHistoryLoading(true);
        Promise.all([
          getStats(card),
          fetchPaymentHistory(card.id),
        ]).then(([s, history]) => {
          statsCache.set(card.id, { stats: s, timestamp: Date.now() });
          setStats(s);
          setPaymentHistory(history);
          setLoading(false);
          setHistoryLoading(false);
        });
      }

      lastOpenedCardIdRef.current = card.id;

      if (card.linked_account_id && user) {
        supabase
          .from('accounts')
          .select('id, name, bank_name, color')
          .eq('id', card.linked_account_id)
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data }) => setLinkedAccount(data as LinkedAccount | null));
      }
    }
  }, [open, card, getStats, user]);

  const fetchPaymentHistory = async (cardId: string): Promise<CardPaymentRecord[]> => {
    if (!user) return [];

    // Fetch last 20 payments for this card
    const { data: payments } = await supabase
      .from('card_payments')
      .select('id, amount, payment_date, payment_method, notes, affects_account_balance, billing_cycle_start, source_account_id')
      .eq('card_id', cardId)
      .eq('user_id', user.id)
      .order('payment_date', { ascending: false })
      .limit(20);

    if (!payments || payments.length === 0) return [];

    // Fetch account names for payments that have source_account_id
    const accountIds = [...new Set(
      payments.filter((p: any) => p.source_account_id).map((p: any) => p.source_account_id as string)
    )];

    let accountMap: Record<string, string> = {};
    if (accountIds.length > 0) {
      const { data: accounts } = await supabase
        .from('accounts')
        .select('id, name')
        .in('id', accountIds)
        .eq('user_id', user.id);
      (accounts ?? []).forEach((a: any) => { accountMap[a.id] = a.name; });
    }

    return (payments as any[]).map(p => ({
      ...p,
      account_name: p.source_account_id ? accountMap[p.source_account_id] ?? null : null,
    })) as CardPaymentRecord[];
  };

  if (!open || !card) return null;

  const currency = card.currency || 'BRL';
  const digits = card.last_four_digits ? `•••• ${card.last_four_digits}` : '•••• ••••';
  const brand = BRAND_CONFIG[card.brand] ?? BRAND_CONFIG['outro'];

  // Derived financial values
  const usedPct = card.limit_amount > 0 ? Math.min(100, ((stats?.usedLimit ?? 0) / card.limit_amount) * 100) : 0;
  const limitColor = usedPct >= 90 ? '#ef4444' : usedPct >= 70 ? '#f59e0b' : '#10b981';
  const daysToClose = stats?.daysToClose ?? 99;
  const daysToDue = stats?.daysToDue ?? 99;
  const pmConfig = PAYMENT_METHOD_LABELS[card.payment_method ?? 'manual'];

  // pendingAmount: how much is still owed — use the computed value from stats
  const pendingAmount = stats?.pendingAmount ?? 0;
  const totalPaid = stats?.totalPaid ?? 0;
  const currentInvoice = stats?.currentInvoice ?? 0;
  const invoiceFullyPaid = totalPaid >= currentInvoice && currentInvoice > 0;

  // Only show payment buttons when there's actually something to pay
  const canShowPaymentActions = !card.is_archived && !loading && pendingAmount > 0;

  const displayTxs: CardTransaction[] = tab === 'fatura'
    ? (stats?.currentCycleTransactions ?? [])
    : (stats?.allRecentTransactions ?? []);

  // Alerts
  const alerts: { icon: string; text: string; color: string; bg: string }[] = [];
  if (daysToClose <= 3) alerts.push({ icon: 'ri-time-line', text: `Fatura fecha em ${daysToClose} dia${daysToClose !== 1 ? 's' : ''}`, color: 'text-red-600', bg: 'bg-red-50 border-red-100' });
  else if (daysToClose <= 7) alerts.push({ icon: 'ri-time-line', text: `Fatura fecha em ${daysToClose} dias`, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' });
  if (daysToDue <= 3) alerts.push({ icon: 'ri-calendar-event-line', text: `Vencimento em ${daysToDue} dia${daysToDue !== 1 ? 's' : ''}`, color: 'text-red-600', bg: 'bg-red-50 border-red-100' });
  if (usedPct >= 90) alerts.push({ icon: 'ri-error-warning-line', text: `Limite quase esgotado (${usedPct.toFixed(0)}%)`, color: 'text-red-600', bg: 'bg-red-50 border-red-100' });
  else if (usedPct >= 75) alerts.push({ icon: 'ri-alert-line', text: `Limite com alto uso (${usedPct.toFixed(0)}%)`, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' });
  if (card.payment_method === 'automatic_debit' && linkedAccount) {
    alerts.push({ icon: 'ri-loop-right-line', text: `Débito automático ativo — ${linkedAccount.name}`, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-100' });
  }
  if (invoiceFullyPaid) {
    alerts.push({ icon: 'ri-check-double-line', text: 'Fatura quitada neste ciclo', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-100' });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal container: fixed height, scrollable body */}
      <div
        className="relative z-10 bg-white rounded-2xl max-w-lg w-full mx-4 flex flex-col overflow-hidden"
        style={{ maxHeight: 'min(90vh, 780px)' }}
      >
        {/* ── Fixed: Card face header ── */}
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)`, padding: '20px 24px 16px' }}
        >
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10 bg-white" style={{ transform: 'translate(35%,-35%)' }} />
          <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full opacity-10 bg-white" style={{ transform: 'translate(-35%,35%)' }} />

          <div className="flex items-start justify-between mb-5 relative z-10">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-white/60 text-xs">{card.bank || 'Cartão'}{card.bank_code ? ` · ${card.bank_code}` : ''}</p>
                {/* Card type badge */}
                {(() => {
                  const ct = CARD_TYPE_CONFIG[card.card_type ?? 'credit'];
                  return (
                    <span className={`flex items-center gap-1 text-[10px] font-600 px-1.5 py-0.5 rounded-full ${ct.bg} ${ct.color}`}>
                      <i className={`${ct.icon}`} style={{ fontSize: '10px' }} />
                      {ct.label}
                    </span>
                  );
                })()}
              </div>
              <p className="text-white text-lg font-700">{card.name}</p>
              {card.nickname && <p className="text-white/60 text-xs">{card.nickname}</p>}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/40 text-white cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>

          <div className="flex items-end justify-between relative z-10">
            <p className="text-white/80 text-sm font-mono tracking-widest">{digits}</p>
            <div className="flex flex-col items-end gap-1">
              <div className="px-2 py-0.5 rounded-md flex items-center" style={{ background: brand.bg }}>
                <span className="text-xs font-800 tracking-wider" style={{ color: brand.textColor, fontSize: '9px' }}>
                  {brand.shortLabel}
                </span>
              </div>
              <span className="text-white/60 text-xs">{brand.label} · {currency}</span>
            </div>
          </div>
        </div>

        {/* ── Scrollable body (all content between header and footer) ── */}
        <div className="flex-1 overflow-y-auto min-h-0">

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="px-4 pt-3 pb-1 space-y-1.5">
              {alerts.map((al, i) => (
                <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-500 ${al.bg} ${al.color}`}>
                  <i className={`${al.icon} text-sm`} />
                  {al.text}
                </div>
              ))}
            </div>
          )}

          {/* Limit bar */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-600 text-gray-600">Limite utilizado</span>
              <span className="text-xs font-700 text-gray-800">
                {loading ? '...' : `${fmt(stats?.usedLimit ?? 0, currency)} de ${fmt(card.limit_amount, currency)}`}
              </span>
            </div>
            <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700 ease-out"
                style={{ width: `${usedPct}%`, backgroundColor: limitColor }} />
            </div>
            <div className="flex justify-between mt-3">
              <div className="bg-white rounded-xl px-3 py-2 border border-gray-100 min-w-[100px]">
                <p className="text-xs text-gray-400 mb-0.5">Disponível</p>
                <p className={`text-sm font-700 ${(stats?.availableLimit ?? card.limit_amount) <= 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                  {loading ? '...' : fmt(stats?.availableLimit ?? card.limit_amount, currency)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">Fecha dia</p>
                <p className={`text-sm font-700 ${daysToClose <= 3 ? 'text-red-600' : daysToClose <= 7 ? 'text-amber-600' : 'text-gray-700'}`}>
                  {card.closing_day}
                  {stats && <span className="text-xs font-400 ml-1">({daysToClose}d)</span>}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-400">Vence dia</p>
                <p className={`text-sm font-700 ${daysToDue <= 3 ? 'text-red-600' : 'text-gray-700'}`}>
                  {card.due_day}
                  {stats && <span className="text-xs font-400 ml-1">({daysToDue}d)</span>}
                </p>
              </div>
            </div>
          </div>

          {/* Invoice summary */}
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-amber-50 rounded-xl px-3 py-2.5 border border-amber-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <i className="ri-file-list-3-line text-amber-500 text-xs" />
                  <p className="text-xs text-amber-600 font-600">Fatura atual</p>
                </div>
                <p className="text-lg font-700 text-amber-700">
                  {loading ? '...' : fmt(currentInvoice, currency)}
                </p>
                {stats && (
                  <p className="text-xs text-amber-500 mt-0.5">
                    {fmtDate(stats.currentCycleStart)} – {fmtDate(stats.currentCycleEnd)}
                  </p>
                )}
              </div>
              <div className="bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                <div className="flex items-center gap-1.5 mb-1">
                  <i className="ri-calendar-2-line text-gray-400 text-xs" />
                  <p className="text-xs text-gray-500 font-600">Próxima fatura</p>
                </div>
                <p className="text-lg font-700 text-gray-700">
                  {loading ? '...' : fmt(stats?.nextInvoice ?? 0, currency)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">Estimada</p>
              </div>
            </div>

            {/* Payments breakdown */}
            {!loading && totalPaid > 0 && (
              <div className="mt-2 space-y-1.5">
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-xl">
                  <i className="ri-check-double-line text-emerald-500 text-sm" />
                  <p className="text-xs text-emerald-700 font-500">
                    {fmt(totalPaid, currency)} pago neste ciclo
                  </p>
                </div>
                {pendingAmount > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-100 rounded-xl">
                    <i className="ri-time-line text-amber-500 text-sm" />
                    <p className="text-xs text-amber-700 font-500">
                      {fmt(pendingAmount, currency)} ainda pendente
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Actions — only shown when pendingAmount > 0 */}
          {canShowPaymentActions && (
            <div className="px-6 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-700 text-gray-600 mb-2">Pagar fatura</p>
              <div className="flex gap-2">
                <button
                  onClick={() => stats && onPayFull?.(card, stats)}
                  className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-600 cursor-pointer whitespace-nowrap transition-colors flex-1 justify-center"
                >
                  <i className="ri-money-dollar-circle-line text-sm" />
                  {`Pagar ${fmt(pendingAmount, currency)}`}
                </button>
                <button
                  onClick={() => stats && onPayPartial?.(card, stats)}
                  className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 hover:bg-white text-gray-700 rounded-xl text-xs font-600 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-percent-line text-sm" />
                  Parcial
                </button>
              </div>
              {totalPaid > 0 && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  Já pago: {fmt(totalPaid, currency)} · Restante: {fmt(pendingAmount, currency)}
                </p>
              )}
            </div>
          )}

          {/* Linked account + payment method */}
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-400 mb-1">Conta vinculada</p>
                {linkedAccount ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: linkedAccount.color || '#10b981' }} />
                    <p className="text-sm font-600 text-gray-800 truncate">{linkedAccount.name}</p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">Não vinculada</p>
                )}
                {linkedAccount?.bank_name && (
                  <p className="text-xs text-gray-400 mt-0.5">{linkedAccount.bank_name}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Pagamento da fatura</p>
                <div className="flex items-center gap-1.5">
                  <i className={`${pmConfig.icon} text-sm ${pmConfig.color}`} />
                  <p className={`text-sm font-600 ${pmConfig.color}`}>{pmConfig.label}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tab + Transactions */}
          <div>
            <div className="flex items-center gap-1 px-6 py-3 border-b border-gray-100">
              {(['fatura', 'todas', 'pagamentos'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-600 cursor-pointer transition-colors whitespace-nowrap ${
                    tab === t ? 'bg-emerald-100 text-emerald-700' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {t === 'fatura' ? 'Fatura atual' : t === 'todas' ? 'Todas as compras' : 'Pagamentos'}
                  {t === 'fatura' && stats && stats.currentCycleTransactions.length > 0 && (
                    <span className="ml-1.5 bg-amber-200 text-amber-800 text-xs px-1.5 py-0.5 rounded-full">
                      {stats.currentCycleTransactions.length}
                    </span>
                  )}
                  {t === 'pagamentos' && paymentHistory.length > 0 && (
                    <span className="ml-1.5 bg-emerald-200 text-emerald-800 text-xs px-1.5 py-0.5 rounded-full">
                      {paymentHistory.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {tab === 'pagamentos' ? (
              <PaymentHistoryTab
                payments={paymentHistory}
                loading={historyLoading}
                currency={currency}
              />
            ) : loading ? (
              <div className="px-6 py-4 space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            ) : displayTxs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <i className="ri-receipt-line text-gray-400 text-lg" />
                </div>
                <p className="text-sm font-600 text-gray-500 mb-1">
                  {tab === 'fatura' ? 'Nenhuma compra nesta fatura' : 'Nenhuma compra registrada'}
                </p>
                <p className="text-xs text-gray-400">Registre despesas com este cartão na tela de Lançamentos</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {displayTxs.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between px-6 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-up-line text-red-500 text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-600 text-gray-800 truncate">{tx.description}</p>
                        <p className="text-xs text-gray-400">
                          {tx.category} · {fmtDate(tx.date)}
                          {tx.installments > 1 && (
                            <span className="ml-1 text-gray-400">({tx.current_installment}/{tx.installments}x)</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-500 ${STATUS_STYLE[tx.status] || 'bg-gray-100 text-gray-500'}`}>
                        {tx.status === 'pago' ? 'Pago' : 'Pendente'}
                      </span>
                      <p className="text-sm font-700 text-red-500">-{fmt(tx.amount, currency)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          {card.notes && (
            <div className="px-6 py-3 bg-amber-50 border-t border-amber-100">
              <p className="text-xs text-amber-700 font-500 mb-0.5">Observação</p>
              <p className="text-sm text-amber-800">{card.notes}</p>
            </div>
          )}
        </div>

        {/* ── Fixed: Footer actions ── */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3 flex-shrink-0 bg-white">
          <p className="text-xs text-gray-400 flex-1">
            Criado em {new Date(card.created_at).toLocaleDateString('pt-BR')}
          </p>
          {card.is_archived ? (
            <span className="text-xs text-amber-600 font-600 bg-amber-50 px-3 py-1.5 rounded-lg">Arquivado</span>
          ) : (
            <button
              onClick={() => { onClose(); onArchive(card); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-amber-600 hover:bg-amber-50 rounded-lg text-xs font-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-archive-line" />Arquivar
            </button>
          )}
          <button
            onClick={() => { onClose(); onEdit(card); }}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line" />Editar
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Payment History Tab Component ────────────────────────────────────────────
interface PaymentHistoryTabProps {
  payments: CardPaymentRecord[];
  loading: boolean;
  currency: string;
}

const fmt = (val: number, currency = 'BRL') => {
  const CURRENCY_MAP: Record<string, string> = { BRL: 'BRL', USD: 'USD', EUR: 'EUR' };
  try {
    return val.toLocaleString('pt-BR', { style: 'currency', currency: CURRENCY_MAP[currency] ?? 'BRL' });
  } catch {
    return `${currency} ${val.toFixed(2)}`;
  }
};

const fmtFullDate = (d: string) =>
  new Date(d + 'T12:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

const PaymentHistoryTab = ({ payments, loading, currency }: PaymentHistoryTabProps) => {
  if (loading) {
    return (
      <div className="px-6 py-4 space-y-3">
        {[1, 2, 3].map(i => <div key={i} className="h-14 bg-gray-100 rounded-xl animate-pulse" />)}
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
          <i className="ri-history-line text-emerald-400 text-lg" />
        </div>
        <p className="text-sm font-600 text-gray-500 mb-1">Nenhum pagamento registrado</p>
        <p className="text-xs text-gray-400">Os pagamentos de fatura aparecerão aqui</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-50">
      {payments.map(p => {
        const methodInfo = PAYMENT_METHOD_DISPLAY[p.payment_method] ?? {
          label: p.payment_method,
          icon: 'ri-money-dollar-circle-line',
        };

        return (
          <div key={p.id} className="flex items-center gap-3 px-6 py-3.5">
            {/* Icon */}
            <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className={`${methodInfo.icon} text-emerald-500 text-base`} />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-sm font-600 text-gray-800">{fmt(p.amount, currency)}</p>
                {p.affects_account_balance && p.account_name && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-sky-50 text-sky-600 rounded-full font-500 whitespace-nowrap">
                    <i className="ri-bank-line mr-0.5" />
                    {p.account_name}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-gray-400">
                  {fmtFullDate(p.payment_date)}
                </span>
                <span className="text-gray-200">·</span>
                <span className="text-xs text-gray-500 font-500">{methodInfo.label}</span>
                {p.notes && (
                  <>
                    <span className="text-gray-200">·</span>
                    <span className="text-xs text-gray-400 italic truncate max-w-[120px]">{p.notes}</span>
                  </>
                )}
              </div>
            </div>

            {/* Badge */}
            <div className="flex-shrink-0">
              <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full font-600">
                Pago
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
